import axios from 'axios'
import toast from 'react-hot-toast'

// Debug-Logging nur in Development
const isDev = import.meta.env.DEV
const log = isDev ? console.log.bind(console) : () => {}

// Base API client
const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Sitzungstoken: wird beim Login ausgestellt und ab dann bei jedem Aufruf
// mitgeschickt. Ohne das wirkte die Anmeldung nur im Browser, die API war
// für jeden offen.
export const SITZUNG_SCHLUESSEL = 'sitzung_token'

export function sitzungToken(): string | null {
  return localStorage.getItem(SITZUNG_SCHLUESSEL)
}

export function sitzungSetzen(token: string | null) {
  if (token) {
    localStorage.setItem(SITZUNG_SCHLUESSEL, token)
  } else {
    localStorage.removeItem(SITZUNG_SCHLUESSEL)
  }
}

// Request interceptor for logging
api.interceptors.request.use(
  (config) => {
    log(`🔄 API Request: ${config.method?.toUpperCase()} ${config.url}`)
    const token = sitzungToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    console.error('❌ API Request Error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    log(`✅ API Response: ${response.status} ${response.config.url}`)
    return response
  },
  (error) => {
    console.error('❌ API Response Error:', error)
    
    if (error.response) {
      const message = error.response.data?.detail || error.response.data?.message || 'API Error'
      toast.error(`API Fehler: ${message}`)
    } else if (error.request) {
      toast.error('Keine Verbindung zum Backend')
    } else {
      toast.error('Unbekannter Fehler')
    }
    
    return Promise.reject(error)
  }
)

// ==================== EXPORTE ====================

export interface ExportKanal {
  kanal_id: string
  slug: string
  name: string
  page_id: string
  instagram_account_id?: string
  ist_aktiv: boolean
  monate: number
  auftrag_id?: string
  letzte_art?: 'erstlauf' | 'monatslauf' | 'nachlauf' | 'nur_texte'
  letzter_status?: 'wartend' | 'laeuft' | 'fertig' | 'fehler' | 'abgebrochen'
  letzter_auftrag_am?: string
  gestartet_am?: string
  beendet_am?: string
  letzter_fehler?: string
  // Vom letzten abgeschlossenen Auftrag, nicht vom juengsten: bleibt gesetzt,
  // waehrend bereits ein neuer Auftrag wartet oder laeuft.
  letzter_erfolg_am?: string
  erfolg_auftrag_id?: string
  stufen_fertig?: number
  stufen_fehler?: number
  stufen_warnung?: number
  stufen_gesamt?: number
  letzte_regung?: string
  pruef_status?: 'fertig' | 'warnung' | 'fehler'
  pruef_details?: {
    ampel: 'gruen' | 'gelb' | 'rot'
    zusammenfassung: string
    befunde: Array<{ schwere: string; punkt: string; text: string }>
    zahlen: Record<string, unknown>
  }
}

export interface ExportAuftrag {
  id: string
  kanal_id: string
  art: string
  status: string
  angelegt_am: string
  gestartet_am?: string
  beendet_am?: string
  letzter_fehler?: string
  export_kanaele?: { name: string; slug: string }
}

export interface ExportStufe {
  stufe: string
  plattform?: string
  status: 'laeuft' | 'fertig' | 'warnung' | 'fehler' | 'uebersprungen'
  fortschritt: number
  fortschritt_gesamt?: number
  objekte?: number
  begonnen_am: string
  beendet_am?: string
  dauer_ms?: number
  meldung?: string
}

// API Types
export interface FacebookPage {
  id: string
  page_id: string
  page_name: string
  category?: string
  description?: string
  followers_count: number
  profile_picture_url?: string  // 🖼️ FEATURE: Profilbild URL
  has_instagram_account?: boolean  // 📸 FEATURE: Instagram-Account Status
  is_active: boolean
  created_at: string
  updated_at: string
}

// 🔥 NEW: Login Types
export interface LoginRequest {
  email: string
  pass: string
}

export interface LoginResponse {
  status: string
  fullname: string
  COOKIENAME: string
  COOKIEVALUE: string
}

export interface Permission {
  id: string
  description: string
}

export interface UserPermissions {
  permissions: Permission[]
}

// 👥 NEW: Multi-User System Types
export interface DashboardUser {
  id: string
  email: string
  full_name: string
  role: 'admin' | 'user'
  is_active: boolean
  created_at: string
  updated_at: string
  pageCount?: number
  earnings30d?: number
}

export interface PageAssignment {
  id: string
  user_id: string
  page_id: string
  assigned_at: string
  is_active: boolean
}

export interface CachedData {
  success: boolean
  data?: any
  source: 'cache' | 'none'
  cache_type: string
  page_id: string
  message?: string
}

export interface UserSummary {
  user: DashboardUser
  pages: FacebookPage[]
  stats: {
    total_pages: number
    earnings_30d: number
    is_admin: boolean
  }
}

export interface CreateUserRequest {
  email: string
  full_name: string
  role: 'admin' | 'user'
}

export interface UpdateUserRequest {
  email?: string
  full_name?: string
  role?: 'admin' | 'user'
}

export interface EarningsData {
  id: string
  page_id: string
  date: string
  earnings_usd: number
  currency: string
  created_at: string
}

export interface PerformanceData {
  id: string
  page_id: string
  date: string
  impressions: number
  engagement: number
  new_followers: number
  video_views: number
  reach: number
  created_at: string
}

export interface PostData {
  id: string
  page_id: string
  post_id: string
  created_time: string
  message?: string
  story?: string
  status_type?: string
  permalink_url?: string
  full_picture_url?: string
  facebook_post_insights?: PostInsight[]
}

export interface PostInsight {
  impressions: number
  reach: number
  engagement: number
  likes: number
  comments: number
  shares: number
  clicks: number
}

export interface StoryData {
  id: string
  story_id: string
  platform: 'facebook' | 'instagram'
  created_time: string
  expires_at?: string
  message?: string
  media_url?: string
  media_type?: string
  media_content_base64?: string
  media_mime_type?: string
  media_size_bytes?: number
  media_checksum?: string
  media_downloaded?: boolean
  media_downloaded_at?: string
  media_download_attempts?: number
  media_download_error?: string
  permalink_url?: string
  impressions: number
  reach: number
  engagement: number
  reactions: number
  replies: number
  link_clicks: number
  taps_forward?: number
  taps_back?: number
  exits?: number
  earnings_usd: number
  metrics_fetched: boolean
  metrics_fetched_at?: string
}

export interface SchedulerJobRun {
  status: 'success' | 'warning' | 'error'
  started_at: string | null
  finished_at?: string | null
  duration_ms?: number | null
  details?: Record<string, any> | null
  error_message?: string | null
}

export interface SchedulerJobStatus {
  job_name: string
  status: 'ok' | 'warning' | 'error' | 'unknown'
  last_run_at?: string | null
  last_run_status?: string | null
  last_success_at?: string | null
  last_error_at?: string | null
  last_error_message?: string | null
  duration_ms?: number | null
  details?: Record<string, any> | null
  runs?: SchedulerJobRun[]
  consecutive_failures?: number
}

export interface SchedulerStatusResponse {
  success: boolean
  generated_at: string
  jobs: SchedulerJobStatus[]
}

export interface DashboardData {
  page_info: FacebookPage
  summary: {
    total_earnings_30d: number
    total_posts_7d: number
    avg_impressions: number
    avg_engagement: number
    total_new_followers: number
  }
  earnings: {
    recent: EarningsData[]
    total_30d: number
    charts: {
      labels: string[]
      values: number[]
      total: number
      average: number
      trend_percentage: number
    }
  }
  performance: {
    recent: PerformanceData[]
    charts: {
      impressions: { labels: string[]; values: number[] }
      engagement: { labels: string[]; values: number[] }
      followers: { labels: string[]; values: number[] }
      video_views: { labels: string[]; values: number[] }
      totals: {
        impressions: number
        engagement: number
        new_followers: number
        video_views: number
      }
    }
  }
  posts: {
    recent: PostData[]
    count_7d: number
    top_performing: Array<{
       id: string
       message: string
       created_time: string
       permalink_url: string
       engagement: number
       impressions: number
       likes: number
       engagement_rate: number
       performance_score: number
     }>
  }
}

export interface AutoPostEntry {
  id: string
  page_id: string
  post_id: string
  created_time: string
  message?: string
  status_type?: string
  auto_post_status?: 'posted' | 'skipped' | 'error' | null
  auto_post_reason?: string | null
  auto_post_checked_at?: string | null
  auto_post_to_instagram: boolean
  instagram_media_id?: string | null
}

export interface AutoPostPageActivity {
  page_id: string
  page_uuid: string
  page_name: string
  instagram_account_id?: string | null
  automatic_post_instagram: boolean
  posts: AutoPostEntry[]
}

export interface AppToken {
  id: string
  token_type: 'user' | 'app'
  label?: string | null
  access_token: string
  expires_at?: string | null
  last_refreshed_at?: string | null
  created_at?: string
  updated_at?: string
  is_active: boolean
}

export interface ApiResponse<T> {
  success: boolean
  data?: T
  message?: string
  error?: string
}

// Viral Tracking Types
export interface ViralTracking {
  id: string
  page_id: string
  content_id: string
  content_type: 'reel' | 'story'
  platform: 'facebook' | 'instagram'
  content_uuid?: string
  detected_at: string
  tracking_until: string
  is_active: boolean
  total_snapshots: number
  last_snapshot_at?: string
  last_impressions: number
  last_reach: number
  last_plays: number
  last_likes: number
  last_comments: number
  last_shares: number
  created_at: string
  updated_at: string
}

export interface ViralTimelineEntry {
  id: string
  tracking_id: string
  captured_at: string
  impressions: number
  reach: number
  plays: number
  likes: number
  comments: number
  shares: number
  saves: number
  reactions_total: number
  video_views: number
  video_views_10_seconds: number
  video_views_30_seconds: number
  video_views_complete: number
  total_watch_time_ms: number
  average_watch_time_ms: number
  replies: number
  exits: number
  taps_forward: number
  taps_back: number
  engagement_rate: number
  created_at: string
}

export interface ViralTopPerformer {
  content_id: string
  content_type: string
  platform: string
  last_impressions: number
  last_likes: number
  total_snapshots: number
}

export interface ViralStats {
  active_trackings: number
  completed_trackings: number
  total_trackings: number
  active_reels: number
  active_stories: number
  active_facebook: number
  active_instagram: number
  total_snapshots: number
  avg_snapshots_per_tracking: number
  configured_pages: number
  top_performers: ViralTopPerformer[]
}

// API Functions
export const facebookApi = {
  // Health check
  async healthCheck() {
    const response = await api.get<{
      status: string
      timestamp: string
      services: {
        database: string
        scheduler: string
        facebook_api: string
      }
    }>('/health')
    return response.data
  },

  // Pages management
  async getPages() {
    const response = await api.get<{
      success: boolean
      count: number
      pages: FacebookPage[]
    }>('/pages')
    return response.data
  },

  async addPage(pageData: { page_id: string; access_token: string }) {
    const response = await api.post<{
      success: boolean
      message: string
      page: FacebookPage
    }>('/pages', pageData)
    return response.data
  },

  // Dashboard data
  async getDashboard(pageId: string, days: number = 30) {
    const response = await api.get<{
      success: boolean
      page_id: string
      days: number
      generated_at: string
      data: DashboardData
    }>(`/dashboard/${pageId}?days=${days}`)
    return response.data
  },

  // Earnings
  async getEarnings(pageId: string, days: number = 30) {
    const response = await api.get<{
      success: boolean
      page_id: string
      days: number
      earnings: EarningsData[]
    }>(`/earnings/${pageId}?days=${days}`)
    return response.data
  },

  // Posts (mit Pagination)
  async getPosts(pageId: string, days: number = 7, limit: number = 50, offset: number = 0) {
    const response = await api.get<{
      success: boolean
      page_id: string
      days: number
      limit: number
      offset: number
      count: number
      posts: PostData[]
    }>(`/posts/${pageId}?days=${days}&limit=${limit}&offset=${offset}`)
    return response.data
  },

  // Stories (mit Pagination)
  async getStories(pageId: string, days: number = 30, platform?: string, limit: number = 50, offset: number = 0) {
    const params = new URLSearchParams({
      days: days.toString(),
      limit: limit.toString(),
      offset: offset.toString()
    })
    if (platform) params.append('platform', platform)
    const response = await api.get<{
      success: boolean
      page_id: string
      days: number
      platform: string
      limit: number
      offset: number
      count: number
      stories: StoryData[]
    }>(`/stories/${pageId}?${params.toString()}`)
    return response.data
  },

  async getAutoPostActivity(hours: number = 48, limit: number = 5) {
    const response = await api.get<{
      success: boolean
      hours: number
      limit: number
      generated_at: string
      pages: AutoPostPageActivity[]
    }>(`/instagram/auto-post/activity?hours=${hours}&limit=${limit}`)
    return response.data
  },

  async runAutoPostJob() {
    const response = await api.post<{
      success: boolean
      message: string
      timestamp: string
    }>('/instagram/auto-post/run')
    return response.data
  },

  // Sync operations
  async manualSync(pageId?: string) {
    const response = await api.post<{
      success: boolean
      message: string
      timestamp: string
    }>('/sync/manual', pageId ? { page_id: pageId } : {})
    return response.data
  },

  async historicalSync(pageId: string, daysBack: number = 60, skipExisting: boolean = true, delaySeconds: number = 3) {
    const response = await api.post<{
      success: boolean
      message: string
      days_back: number
      skip_existing: boolean
      delay_seconds: number
      timestamp: string
    }>('/sync/historical', {
      page_id: pageId,
      days_back: daysBack,
      skip_existing: skipExisting,
      delay_seconds: delaySeconds
    })
    return response.data
  },

  // Token status
  async getTokenStatus(pageId: string) {
    const response = await api.get<{
      success: boolean
      page_id: string
      token_valid: boolean
      status: 'active' | 'invalid' | 'error' | 'no_token'
      message: string
      page_name?: string
      days_remaining: number | null  // null = kein Ablaufdatum (unbegrenzt)
      expires_at: number
      last_checked: string
    }>(`/tokens/status/${pageId}`)
    return response.data
  },

  // 📅 Alle Pages Token-Status mit Tagen
  async getAllPagesTokenStatus() {
    const response = await api.get<{
      success: boolean
      summary: {
        total_pages: number
        valid_tokens: number
        expired_tokens: number
        warning_tokens: number
        health_percentage: number
      }
      pages: Array<{
        success: boolean
        page_id: string
        page_name: string
        token_valid: boolean
        days_remaining: number | null  // null = kein Ablaufdatum (unbegrenzt)
        message: string
        expires_at: number
        last_checked: string
      }>
      checked_at: string
    }>('/pages/token-status')
    return response.data
  },

  // 🔥 NEW: Token Management Functions
  async exchangeLongLivedToken(pageId: string, shortToken: string) {
    const response = await api.post<{
      success: boolean
      message: string
      page_id: string
      days_remaining: number | null  // null = kein Ablaufdatum
      token_length?: number
      expires_in_days?: number
      timestamp: string
    }>('/tokens/exchange-long-lived', {
      page_id: pageId,
      short_token: shortToken
    })
    return response.data
  },

  // 🔧 NEW: Development Debug
  async getDevelopmentDebug() {
    const response = await api.get<{
      success: boolean
      debug_info: {
        app_credentials: {
          app_id_set: boolean
          app_secret_set: boolean
          app_id_preview: string
          app_secret_preview: string
        }
        facebook_api_test: {
          app_info?: {
            status_code: number
            response: any
            app_token_valid: boolean
          }
          api_version?: {
            status_code: number
            response: any
            api_accessible: boolean
          }
          error?: string
        }
        development_mode_notes: {
          app_status: string
          requirements: string[]
          common_issues: string[]
        }
      }
      recommendations: string[]
      timestamp: string
    }>('/tokens/development-debug')
    return response.data
  },

  async refreshToken(pageId: string) {
    const response = await api.post<{
      success: boolean
      message: string
      page_id: string
      timestamp: string
    }>(`/tokens/refresh/${pageId}`)
    return response.data
  },

  async refreshAllTokens() {
    const response = await api.post<{
      success: boolean
      message: string
      timestamp: string
    }>('/tokens/refresh-all')
    return response.data
  },

  async getAppTokens(tokenType?: 'user' | 'app', includeInactive: boolean = false) {
    const params = new URLSearchParams()
    if (tokenType) params.append('token_type', tokenType)
    if (includeInactive) params.append('include_inactive', 'true')
    const response = await api.get<{
      success: boolean
      count: number
      tokens: AppToken[]
    }>(`/tokens/app${params.toString() ? `?${params.toString()}` : ''}`)
    return response.data
  },

  async saveAppToken(payload: { token_type: 'user' | 'app'; access_token: string; label?: string; expires_at?: string }) {
    const response = await api.post<{
      success: boolean
      token: AppToken
    }>('/tokens/app', payload)
    return response.data
  },

  async deactivateAppToken(tokenId: string) {
    const response = await api.post<{
      success: boolean
      token_id: string
      message: string
    }>(`/tokens/app/${tokenId}/deactivate`)
    return response.data
  },

  async getTokenHealthCheck() {
    const response = await api.get<{
      success: boolean
      summary: {
        total_pages: number
        valid_tokens: number
        invalid_tokens: number
        health_score: number
      }
      tokens: Array<{
        page_id: string
        page_name: string
        has_token: boolean
        is_valid: boolean
        status: 'active' | 'invalid' | 'error' | 'no_token'
        message: string
        expires_info?: string
        last_checked: string
      }>
    }>('/tokens/health-check')
    return response.data
  },

  async getAllTokenStatuses() {
    const response = await api.get<{
      success: boolean
      tokens: Record<string, {
        valid: boolean
        status: 'active' | 'invalid' | 'error' | 'no_token'
        message: string
        lastChecked: string
      }>
    }>('/tokens/status-all')
    return response.data
  },

  async getRateLimitDebug(): Promise<any> {
    const response = await api.get('/tokens/rate-limit-debug')
    return response.data
  },

  // 🔥 NEW: Login API Functions
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('https://www.agendi.at/v1/portal/login.php', credentials)
    return response.data
  },

  // 🔐 NEW: Supabase-Login für Multi-User-System
  async loginWithSupabase(credentials: { email: string; password: string }) {
    const response = await api.post<{
      success: boolean
      message: string
      user: {
        id: string
        email: string
        full_name: string
        role: 'admin' | 'user'
        is_active: boolean
      }
      session?: { token: string | null; gueltig_bis: string | null }
    }>('/auth/login', credentials)

    // Das Token gehört sofort in den Speicher, damit alle weiteren Aufrufe
    // dieser Sitzung als angemeldet erkannt werden.
    if (response.data.session?.token) {
      sitzungSetzen(response.data.session.token)
    }
    return response.data
  },

  async getMyPermissions(): Promise<Permission[]> {
    // Verwende den gesetzten Cookie für die Anfrage
    console.log('🔍 API Debug - getMyPermissions called')
    console.log('🔍 API Debug - Current cookies:', document.cookie)
    
    const response = await api.get<Permission[]>('https://www.agendi.at/v1/portal/mypermissions.php', {
      withCredentials: true, // Wichtig: Cookie mitsenden
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    })
    
    console.log('🔍 API Debug - getMyPermissions response:', response.data)
    return response.data
  },

  async checkFacebookPermission(): Promise<boolean> {
    try {
      console.log('🔍 API Debug - Starting getMyPermissions...')
      const permissions = await this.getMyPermissions()
      console.log('🔍 API Debug - Raw permissions response:', permissions)
      
      // Die API gibt ein direktes Array zurück
      if (permissions && Array.isArray(permissions)) {
        console.log('🔍 API Debug - Permissions is direct array:', permissions)
        
        const hasFacebookPermission = permissions.some(perm => {
          console.log('🔍 API Debug - Checking permission:', perm.id, '=== PERM_FACEBOOK?', perm.id === 'PERM_FACEBOOK')
          return perm.id === 'PERM_FACEBOOK'
        })
        
        console.log('🔍 API Debug - Has Facebook Permission:', hasFacebookPermission)
        return hasFacebookPermission
      }
      
      console.log('🔍 API Debug - No valid permissions array found:', permissions)
      return false
    } catch (error) {
      console.error('🔍 API Debug - Error checking Facebook permission:', error)
      return false
    }
  },

  // 👥 NEW: User Management API Functions
  async getAllUsers() {
    const response = await api.get<{
      success: boolean
      count: number
      users: DashboardUser[]
    }>('/facebook/users')
    return response.data
  },

  async getUser(userId: string) {
    const response = await api.get<{
      success: boolean
      user: DashboardUser
    }>(`/facebook/users/${userId}`)
    return response.data
  },

  async createUser(userData: CreateUserRequest) {
    const response = await api.post<{
      success: boolean
      message: string
      user: DashboardUser
    }>('/facebook/users', userData)
    return response.data
  },

  async updateUser(userId: string, updateData: UpdateUserRequest) {
    const response = await api.put<{
      success: boolean
      message: string
      user: DashboardUser
    }>(`/facebook/users/${userId}`, updateData)
    return response.data
  },

  async deactivateUser(userId: string) {
    const response = await api.delete<{
      success: boolean
      message: string
    }>(`/facebook/users/${userId}`)
    return response.data
  },

  async assignPageToUser(userId: string, pageId: string) {
    const response = await api.post<{
      success: boolean
      message: string
      assignment: PageAssignment
    }>(`/facebook/users/${userId}/assign-page/${pageId}`)
    return response.data
  },

  async removePageAssignment(userId: string, pageId: string) {
    const response = await api.delete<{
      success: boolean
      message: string
    }>(`/facebook/users/${userId}/remove-page/${pageId}`)
    return response.data
  },

  async getUserPages(userId: string) {
    const response = await api.get<{
      success: boolean
      count: number
      pages: FacebookPage[]
    }>(`/facebook/users/${userId}/pages`)
    return response.data
  },

  async getUserPagesWithDetails(userId: string) {
    const response = await api.get<{
      success: boolean
      count: number
      pages: Array<FacebookPage & {
        assignment_id: string
        assigned_at: string
        earnings_30d: number
      }>
    }>(`/facebook/users/${userId}/pages-with-details`)
    return response.data
  },

  async getUserSummary(userId: string) {
    const response = await api.get<{
      success: boolean
      summary: UserSummary
    }>(`/facebook/users/${userId}/summary`)
    return response.data
  },

  async getUserEarnings(userId: string, startDate?: string, endDate?: string, days: number = 30) {
    const params = new URLSearchParams()
    if (startDate) params.append('start_date', startDate)
    if (endDate) params.append('end_date', endDate)
    if (days) params.append('days', days.toString())
    
    const response = await api.get<{
      success: boolean
      user_id: string
      start_date: string
      end_date: string
      earnings: {
        total_earnings: number
        daily_earnings: Array<{date: string, earnings: number}>
        page_earnings: Record<string, number>
        summary: {
          total_days: number
          avg_daily_earnings: number
          highest_day: {date: string, earnings: number} | null
          lowest_day: {date: string, earnings: number} | null
        }
      }
    }>(`/facebook/users/${userId}/earnings?${params.toString()}`)
    return response.data
  },

  async getPageUsers(pageId: string) {
    const response = await api.get<{
      success: boolean
      count: number
      users: DashboardUser[]
    }>(`/facebook/pages/${pageId}/users`)
    return response.data
  },

  // 📅 Scheduler Status
  async getSchedulerStatus() {
    const response = await api.get<SchedulerStatusResponse>('/status/scheduler')
    return response.data
  },

  async getCachedData(pageId: string, cacheType: string) {
    const response = await api.get<CachedData>(`/cache/${pageId}/${cacheType}`)
    return response.data
  },

  // 🚀 SMART CACHE FUNCTIONS - Kombiniert Cache mit Fallback
  async getCachedDashboard(pageId: string, days: number = 30) {
    try {
      // Versuche zuerst Cache zu holen
      const cachedData = await this.getCachedData(pageId, 'dashboard')
      
      if (cachedData.success && cachedData.data) {
        console.log('🚀 Dashboard-Daten aus Cache geladen')
        return cachedData
      }
      
      // Fallback: Normale API
      console.log('🔄 Cache-Miss - Lade Dashboard-Daten live...')
      return await this.getDashboard(pageId, days)
      
    } catch (error) {
      console.error('❌ Fehler beim Laden der gecachten Dashboard-Daten:', error)
      // Fallback: Normale API
      return await this.getDashboard(pageId, days)
    }
  },

  async getCachedAnalytics(pageId: string) {
    try {
      // Versuche zuerst Cache zu holen
      const cachedData = await this.getCachedData(pageId, 'analytics')
      
      if (cachedData.success && cachedData.data) {
        console.log('🚀 Analytics-Daten aus Cache geladen')
        return cachedData
      }
      
      // Fallback: Normale API
      console.log('🔄 Cache-Miss - Lade Analytics-Daten live...')
      return await this.getAnalytics()
      
    } catch (error) {
      console.error('❌ Fehler beim Laden der gecachten Analytics-Daten:', error)
      // Fallback: Normale API
      return await this.getAnalytics()
    }
  },

  async getCachedPosts(pageId: string) {
    try {
      // Versuche zuerst Cache zu holen
      const cachedData = await this.getCachedData(pageId, 'posts')
      
      if (cachedData.success && cachedData.data) {
        console.log('🚀 Posts-Daten aus Cache geladen')
        return cachedData
      }
      
      // Fallback: Normale API
      console.log('🔄 Cache-Miss - Lade Posts-Daten live...')
      return await this.getPosts(pageId)
      
    } catch (error) {
      console.error('❌ Fehler beim Laden der gecachten Posts-Daten:', error)
      // Fallback: Normale API
      return await this.getPosts(pageId)
    }
  },

  // 🔄 Manuelles Metriken-Refresh
  async refreshPostMetrics(pageId: string, daysBack: number = 7) {
    const response = await api.post<{
      success: boolean
      message: string
      timestamp: string
    }>(`/posts/${pageId}/refresh-metrics?days_back=${daysBack}`)
    return response.data
  },

  // 🚀 FALLBACK: Normale API-Funktionen für Cache-Miss
  async getAnalytics() {
    // Fallback für Analytics - kann später implementiert werden
    return {
      success: false,
      message: "Analytics API noch nicht implementiert",
      data: null
    }
  },

  // Viral Tracking API
  async getViralActive() {
    const response = await api.get<{
      success: boolean
      count: number
      data: ViralTracking[]
    }>('/viral/active')
    return response.data
  },

  async getViralTimeline(contentId: string) {
    const response = await api.get<{
      success: boolean
      content_id: string
      count: number
      data: ViralTimelineEntry[]
    }>(`/viral/timeline/${contentId}`)
    return response.data
  },

  async getViralStats() {
    const response = await api.get<{
      success: boolean
      data: ViralStats
    }>('/viral/stats')
    return response.data
  },

  async triggerViralWatcher() {
    const response = await api.post<{
      success: boolean
      message: string
      timestamp: string
    }>('/viral/watcher/trigger')
    return response.data
  },

  async triggerViralTracker() {
    const response = await api.post<{
      success: boolean
      message: string
      timestamp: string
    }>('/viral/tracker/trigger')
    return response.data
  },

  // ==================== EXPORTE ====================

  async getExportKanaele() {
    const response = await api.get<{ success: boolean; kanaele: ExportKanal[] }>(
      '/exports/kanaele'
    )
    return response.data
  },

  async getVerfuegbareSeiten() {
    const response = await api.get<{
      success: boolean
      seiten: Array<{
        id: string
        page_id: string
        page_name: string
        followers_count: number
        instagram_account_id?: string
      }>
      bereits_aufgenommen: number
    }>('/exports/verfuegbare-seiten')
    return response.data
  },

  async kanalAufnehmen(daten: {
    page_id: string
    monate?: number
    ohne_kommentare?: boolean
    wasserzeichen?: string[]
    sofort_starten?: boolean
  }) {
    const response = await api.post<{
      success: boolean
      kanal: ExportKanal
      auftrag?: ExportAuftrag
    }>('/exports/kanaele', daten)
    return response.data
  },

  async kanalAendern(kanalId: string, daten: Record<string, unknown>) {
    const response = await api.patch<{ success: boolean }>(
      `/exports/kanaele/${kanalId}`,
      daten
    )
    return response.data
  },

  async exportStarten(kanalId: string, art: 'erstlauf' | 'monatslauf' | 'nur_texte' = 'monatslauf') {
    const response = await api.post<{ success: boolean; auftrag: ExportAuftrag }>(
      '/exports/auftraege',
      { kanal_id: kanalId, art }
    )
    return response.data
  },

  async getExportAuftrag(auftragId: string) {
    const response = await api.get<{
      success: boolean
      auftrag: ExportAuftrag
      stufen: ExportStufe[]
    }>(`/exports/auftraege/${auftragId}`)
    return response.data
  },

  async exportAbbrechen(auftragId: string) {
    const response = await api.delete<{ success: boolean; hinweis: string }>(
      `/exports/auftraege/${auftragId}`
    )
    return response.data
  },

  async exportKommentareNachholen(kanalId: string) {
    const response = await api.post<{ success: boolean; auftrag_id: string; hinweis: string }>(
      `/exports/kanaele/${kanalId}/kommentare-nachholen`
    )
    return response.data
  },

  async exportErneutLiefern(kanalId: string) {
    const response = await api.post<{ success: boolean; auftrag_id: string; hinweis: string }>(
      `/exports/kanaele/${kanalId}/erneut-liefern`
    )
    return response.data
  },

  async getExportAuslastung() {
    const response = await api.get<{
      success: boolean
      fenster_beginn: string
      plattformen: Record<string, { anfragen: number; auslastung: number; gesperrt_bis?: string }>
    }>('/exports/auslastung')
    return response.data
  }
}

export default api 